import { Button, Stack, Text } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { admin } from '@repletoryreach/sdk-api';
import { useQueryClient } from '@tanstack/react-query';
import { formatDateTime } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';

type Tenant = NonNullable<Awaited<ReturnType<typeof admin.listTenants>>['data']>[number];

// W31: POST /v1/admin/impersonate mints a ≤15-minute tenant-scoped token
// (no refresh token, audited). The token travels in the URL hash fragment
// never sent to a server: and the tenant console adopts it into
// sessionStorage, so a real agent session in localStorage is untouched.
export function ImpersonateButton({ tenant }: { tenant: Tenant }) {
  const t = useT();
  const qc = useQueryClient();

  function handleClick() {
    openConfirmModal({
      title: t('tenants.view.impersonate.modal.title', { name: tenant.name }),
      children: (
        <Stack gap="sm">
          <Text fz="sm">{t('tenants.view.impersonate.modal.body', { name: tenant.name })}</Text>
          <Text fz="sm">{t('tenants.view.impersonate.modal.capability')}</Text>
          <Text fz="sm" c="var(--rr-color-text-muted)">{t('tenants.view.impersonate.modal.audited')}</Text>
        </Stack>
      ),
      labels: { confirm: t('tenants.view.impersonate.modal.confirm'), cancel: t('action.cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: () => { void mintAndOpen(); }
    });
  }

  async function mintAndOpen() {
    // The tab has to be requested inside the click that confirmed the modal.
    // Asking for it after the mint resolves puts it outside the user gesture,
    // which browsers block silently: the operator saw nothing happen at all.
    const tab = window.open('', '_blank');
    if (!tab) {
      notifyError(t('tenants.view.impersonate.toast.popupBlocked'), t('tenants.view.impersonate.toast.failedTitle'));
      return;
    }
    try {
      const res = await admin.impersonate(tenant.id);
      const base = (import.meta.env.VITE_TENANT_UI_URL as string | undefined) ?? 'http://localhost:3006';
      tab.location.href = `${base}/#impersonation=${encodeURIComponent(res.data.token)}`;
      // The grant carries its own expiry, so the operator is told when the
      // session dies rather than being left to guess at "short-lived".
      notifySuccess(
        t('tenants.view.impersonate.toast.opened', { name: tenant.name, until: formatDateTime(res.data.expires_at) }),
        t('tenants.view.impersonate.toast.openedTitle')
      );
      void qc.invalidateQueries({ queryKey: ['admin', 'impersonations'] });
    } catch (e) {
      tab.close();
      notifyError(
        e instanceof Error ? e.message : t('tenants.view.impersonate.toast.failed'),
        t('tenants.view.impersonate.toast.failedTitle')
      );
    }
  }

  return (
    // The design makes this the page's primary action, which is what it is:
    // the one control on a tenant that does something to the world.
    <Tooltip label={t('tenants.view.impersonate.tooltip')}>
      <Button size="sm" leftSection={<ImpersonateIcon />} onClick={handleClick}>
        {t('tenants.view.impersonate.button')}
      </Button>
    </Tooltip>
  );
}

function ImpersonateIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="7" cy="4.6" r="2.4" />
      <path d="M2.4 12c0-2.3 2-3.8 4.6-3.8s4.6 1.5 4.6 3.8" />
    </svg>
  );
}
